import { createContext, useContext, useMemo, useCallback } from 'react';
import { useAuth } from '../features/auth/hooks/useAuth';
import { useSchool } from './SchoolContext';
import { useRoleSwitcher } from '../features/auth/hooks/useRoleSwitcher';
import useCollectionSnapshot from '../hooks/useCollectionSnapshot';
import { COLLECTIONS } from '../utils/constants';
import orgChartService from '../services/orgChartService';
import { filterClassScope } from '../services/domain/classScope';
import usePhaseScope from '../hooks/usePhaseScope';
import {
  resolveUserScope,
  getDefaultScopeSettings,
  getClassIdsForLevel,
  isClassInScope as isClassInScopeUtil,
} from '../services/scopeService';

const ScopeContext = createContext();

/**
 * Hook to access the current scope context.
 */
export const useScope = () => {
  const context = useContext(ScopeContext);
  if (!context) {
    return {
      scope: null,
      settings: getDefaultScopeSettings('teacher'),
      activeLevel: 'school',
      setActiveLevel: () => {},
      availableLevels: ['assignments', 'scope', 'school'],
      isInScope: () => true,
      scopeFilter: { classIds: new Set() },
      filterItems: (items) => items || [],
      filterRecords: (items) => items || [],
      userGrades: new Set(),
      classes: [],
      loading: false,
    };
  }
  return context;
};

/**
 * ScopeProvider — wraps app after SchoolProvider + RoleSwitcherProvider.
 *
 * Computes scope from:
 *  - user (AuthContext)
 *  - currentRole (RoleSwitcherContext)
 *  - classes (realtime listener)
 *  - org chart positions (all org charts, polled on mount)
 *
 * Organisation chart is the SOLE authority for scope resolution.
 * class.teacherIds is NOT used for scope.
 */
export const ScopeProvider = ({ children }) => {
  const { user } = useAuth();
  const { schoolId, loading: schoolLoading } = useSchool();
  const { currentRole, loading: roleLoading } = useRoleSwitcher();

  // Load classes via realtime listener
  const identity = useMemo(() => [user?.uid, currentRole], [user?.uid, currentRole]);
  const classesRead = useCollectionSnapshot(COLLECTIONS.CLASSES, undefined, { identity, retainConfirmed: true });
  const { data: allClasses } = classesRead;

  // Load org chart positions via realtime listener (so scope updates when assignments change)
  const orgRead = useCollectionSnapshot('hierarchies', undefined, { identity, retainConfirmed: true });
  const { data: orgCharts } = orgRead;
  const retryClasses = classesRead.retry, retryOrg = orgRead.retry;
  const retryScope = useCallback(() => { retryClasses(); retryOrg(); }, [retryClasses, retryOrg]);

  const allPositions = useMemo(
    () => orgChartService.getAllPositions(orgCharts || []),
    [orgCharts]
  );

  // Resolve scope whenever user, role, classes, or positions change
  const scope = useMemo(() => {
    if (!user || !currentRole) return null;
    return resolveUserScope({
      user,
      role: currentRole,
      allClasses: allClasses || [],
      allPositions,
    });
  }, [user, currentRole, allClasses, allPositions]);

  const loading = schoolLoading || roleLoading || classesRead.loading || orgRead.loading
    || !classesRead.loaded || !orgRead.loaded;
  const phaseScope = usePhaseScope({ schoolId, actorId: user?.uid, role: currentRole,
    classes: allClasses || [], classIds: scope?.scope.classIds, scopeLoading: loading,
    scopeError: classesRead.error || orgRead.error,
    scopeFromCache: classesRead.fromCache || orgRead.fromCache,
    scopeConfirmed: classesRead.confirmed && orgRead.confirmed, retryScope });
  const effectiveLevel = phaseScope.enabled ? phaseScope.level : scope?.defaultLevel || 'scope';
  const setScopeChoice = phaseScope.setScopeChoice;
  const setActiveLevel = useCallback(level => setScopeChoice(level === 'school' ? 'school' : 'scope'),
    [setScopeChoice]);

  // Current scope filter
  const scopeFilter = useMemo(() => {
    if (!scope) return { classIds: new Set() };
    return { classIds: getClassIdsForLevel(scope, effectiveLevel) };
  }, [scope, effectiveLevel]);

  const isInScope = useCallback((classId) => {
    if (!scope) return true;
    return isClassInScopeUtil(scope, effectiveLevel, classId);
  }, [scope, effectiveLevel]);

  // Set of grade values from the user's assigned classes — used for domain visibility scoping
  const userGrades = useMemo(() => {
    const grades = new Set();
    if (!scope || !allClasses) return grades;
    const assignedIds = scope.assignments?.classIds || new Set();
    for (const cls of allClasses) {
      if (assignedIds.has(cls.id) && cls.grade) {
        grades.add(String(cls.grade).trim());
      }
    }
    return grades;
  }, [scope, allClasses]);

  const filterItems = useCallback((items, matchField = 'classId') => {
    if (!scope) return [];
    if (!items || !effectiveLevel) return [];
    if (effectiveLevel === 'school') return items;
    return filterClassScope(items, getClassIdsForLevel(scope, effectiveLevel), allClasses || [], matchField);
  }, [scope, effectiveLevel, allClasses]);

  // No-op: org chart positions now update via realtime listener
  const refreshOrgChart = useCallback(() => {}, []);

  const filterPhaseRecords = phaseScope.filterRecords;
  const filterRecords = useCallback((items, matchField = 'classId') =>
    filterPhaseRecords(filterItems(items, matchField)), [filterPhaseRecords, filterItems]);

  const value = useMemo(() => ({
    scope,
    settings: scope?.settings || getDefaultScopeSettings(currentRole || 'teacher'),
    activeLevel: effectiveLevel,
    setActiveLevel,
    availableLevels: phaseScope.options.some(option => option.key === 'school') ? ['scope', 'school'] : ['scope'],
    isInScope,
    scopeFilter,
    filterItems,
    filterRecords,
    phaseScope,
    userGrades,
    classes: allClasses || [],
    loading,
    refreshOrgChart,
  }), [scope, effectiveLevel, setActiveLevel, isInScope, scopeFilter, filterItems, filterRecords, phaseScope, userGrades, allClasses, loading, currentRole, refreshOrgChart]);

  return (
    <ScopeContext.Provider value={value}>
      {children}
    </ScopeContext.Provider>
  );
};
